import axios from 'axios';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);

// Determine the API base URL dynamically
const getBaseUrl = () => {
  // If environment variable is set, use it
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }

  // Otherwise, derive it from the current host
  const { protocol, hostname, port } = window.location;

  // If we're running on localhost with a specific port for frontend
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    // For local development, the backend is typically running on port 8020
    return 'http://localhost:8020';
  }

  // For production, use the same origin since the FastAPI backend serves the frontend
  // The backend handles API routes with the /api prefix
  return window.location.origin;
};

// Create an axios instance with default config
const api = axios.create({
  baseURL: getBaseUrl(),
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add a request interceptor to include the token in all requests
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Add a response interceptor to handle authentication errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Handle 401 Unauthorized errors
    if (error.response && error.response.status === 401) {
      // Clear token from localStorage
      localStorage.removeItem('token');

      // Redirect to login page if not already there
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// API functions for authentication
export const authService = {
  // Login user
  login: async (username, password) => {
    try {
      const formData = new FormData();
      formData.append('username', username);
      formData.append('password', password);

      const response = await api.post('/api/auth/token', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      return response.data;
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  },

  // Register user
  register: async (username, email, password) => {
    try {
      const response = await api.post('/api/auth/register', {
        username,
        email,
        password
      });
      return response.data;
    } catch (error) {
      console.error('Register error:', error);
      throw error;
    }
  },

  // Get user by ID
  getUserById: async (userId) => {
    try {
      const response = await api.get(`/api/auth/users/${userId}`);
      return response.data;
    } catch (error) {
      console.error('Error getting user by ID:', error);
      throw error;
    }
  },

  // Get current user
  getCurrentUser: async () => {
    try {
      const response = await api.get('/api/auth/me');
      return response.data;
    } catch (error) {
      console.error('Error getting current user:', error);
      throw error;
    }
  },
};

// API functions for clinics
export const clinicService = {
  // Get all clinics with pagination and search
  getClinics: async (page = 1, search = '', status = 'all') => {
    try {
      const response = await api.get(`/api/clinics`, {
        params: {
          page,
          search: search || undefined,
          status: status !== 'all' ? status : undefined
        },
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching clinics:', error);
      throw error;
    }
  },

  // Search clinic configurations using the new search endpoint
  searchClinicConfigurations: async (filters = {}) => {
    try {
      console.log('Searching clinic configurations with filters:', filters);
      const response = await api.post('/api/clinic-configuration/search', filters);
      console.log('Search response:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error searching clinic configurations:', error);
      throw error;
    }
  },

  // Create a new clinic
  createClinic: async (clinicData) => {
    try {
      // Use the new clinic configuration endpoint
      const response = await api.post('/api/clinic-configuration/create', {
        clid: clinicData.id, // Use the provided ID field
        name: clinicData.name,
        region: clinicData.region || 'HK' // Use provided region or default to HK
      });
      return response.data;
    } catch (error) {
      console.error('Error creating clinic:', error);
      throw error;
    }
  },

  // Get clinic details
  getClinicDetails: async (clinicId) => {
    try {
      const response = await api.get(`/api/clinics/${clinicId}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching clinic ${clinicId}:`, error);
      throw error;
    }
  },

  getClinicCopilotSettings: async (clinicId) => {
    const response = await api.get(`/api/clinics/${clinicId}/settings`);
    return response.data;
  },

  patchClinicCopilotSettings: async (clinicId, copilotRewritePrompt) => {
    const response = await api.patch(`/api/clinics/${clinicId}/settings`, {
      copilot_rewrite_prompt: copilotRewritePrompt,
    });
    return response.data;
  },

  // Alias for getClinicDetails for backward compatibility
  getClinicById: async (clinicId) => {
    return clinicService.getClinicDetails(clinicId);
  },

  // Update clinic configuration
  updateClinic: async (clinicId, clinicData) => {
    try {
      const response = await api.put(`/api/clinics/${clinicId}`, clinicData);
      return response.data;
    } catch (error) {
      console.error(`Error updating clinic ${clinicId}:`, error);
      throw error;
    }
  },

  // Update clinic status
  updateClinicStatus: async (clinicId, status) => {
    try {
      const response = await api.put(`/api/clinics/${clinicId}/status`, { status });
      return response.data;
    } catch (error) {
      console.error(`Error updating clinic ${clinicId} status:`, error);
      throw error;
    }
  },

  // Update clinic ID
  updateClinicId: async (clinicId, newId) => {
    try {
      const response = await api.put(`/api/clinics/${clinicId}/update-id`, { new_id: newId });
      return response.data;
    } catch (error) {
      console.error(`Error updating clinic ${clinicId} ID:`, error);
      throw error;
    }
  },

  // Get supported locale options (value + label) for clinic settings, from backend
  getSupportedLocaleOptions: async () => {
    try {
      const response = await api.get('/api/settings/supported-locales');
      return response.data;
    } catch (error) {
      console.error('Error fetching supported locale options:', error);
      throw error;
    }
  },

  // Get clinic documents
  getClinicDocuments: async (clinicId) => {
    try {
      const response = await api.get(`/api/clinics/${clinicId}/documents`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching documents for clinic ${clinicId}:`, error);
      // Return empty documents array on error to prevent UI crashes
      return { documents: [] };
    }
  },

  // Get clinic evaluations
  getClinicEvaluations: async (clinicId, skip = 0, limit = 100) => {
    try {
      const response = await api.get(`/api/clinics/${clinicId}/evaluations`, {
        params: { skip, limit }
      });
      return response.data;
    } catch (error) {
      console.error(`Error fetching evaluations for clinic ${clinicId}:`, error);
      // Return empty evaluations array on error to prevent UI crashes
      return { items: [], total: 0, skip: 0, limit: 100 };
    }
  },

  // Get evaluation detail (lazy loading)
  getEvaluationDetail: async (clinicId, evaluationId) => {
    try {
      const response = await api.get(`/api/clinics/${clinicId}/evaluations/${evaluationId}/detail`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching evaluation detail ${evaluationId}:`, error);
      throw error;
    }
  },

  // Download evaluation result as CSV
  downloadEvaluationResult: async (clinicId, evaluationId) => {
    try {
      const response = await api.get(
        `/api/clinics/${clinicId}/evaluations/${evaluationId}/export`,
        {
          responseType: 'blob', // Important for file download
        }
      );

      // Create blob and download
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `evaluation_${evaluationId}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error(`Error downloading evaluation result ${evaluationId}:`, error);
      throw error;
    }
  },

  // Add a document to a clinic
  addClinicDocument: async (clinicId, documentData) => {
    try {
      const response = await api.post(`/api/clinics/${clinicId}/documents`, documentData);
      return response.data;
    } catch (error) {
      console.error(`Error adding document to clinic ${clinicId}:`, error);
      throw error;
    }
  },

  // Update a clinic document
  updateClinicDocument: async (clinicId, documentId, documentData) => {
    try {
      const response = await api.put(`/api/clinics/${clinicId}/documents/${documentId}`, documentData);
      return response.data;
    } catch (error) {
      console.error(`Error updating document ${documentId} for clinic ${clinicId}:`, error);
      throw error;
    }
  },

  // Delete a clinic document
  deleteClinicDocument: async (clinicId, documentId) => {
    try {
      const response = await api.delete(`/api/clinics/${clinicId}/documents/${documentId}`);
      return response.data;
    } catch (error) {
      console.error(`Error deleting document ${documentId} for clinic ${clinicId}:`, error);
      throw error;
    }
  },

  // Get conversations for a clinic
  getClinicConversations: async (clinicId, page = 1, perPage = 5, search = '') => {
    try {
      const response = await api.get(`/api/conversations/clinic/${clinicId}`, {
        params: { page, per_page: perPage, search: search || undefined },
      });
      return response.data;
    } catch (error) {
      console.error(`Error fetching conversations for clinic ${clinicId}:`, error);
      throw error;
    }
  },

  // Get conversation details
  getConversationDetails: async (clinicId, conversationId) => {
    try {
      const response = await api.get(`/api/conversations/clinic/${clinicId}/detail/${conversationId}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching conversation ${conversationId}:`, error);
      throw error;
    }
  },

  // Alias for getConversationDetails for backward compatibility
  getConversationDetail: async (clinicId, conversationId) => {
    return clinicService.getConversationDetails(clinicId, conversationId);
  },

  // Get messages for a conversation
  getConversationMessages: async (conversationId) => {
    try {
      const response = await api.get(`/api/conversations/${conversationId}/messages`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching messages for conversation ${conversationId}:`, error);
      throw error;
    }
  },

  // Get earlier messages for a conversation before a specific message ID
  getConversationMessagesBefore: async (conversationId, cursor) => {
    try {
      const response = await api.get(`/api/conversations/${conversationId}/messages`, {
        params: { before_id: cursor, limit: 60 }
      });
      return response.data;
    } catch (error) {
      console.error(`Error fetching earlier messages for conversation ${conversationId}:`, error);
      throw error;
    }
  },

  // Check if the documents table exists
  checkDocumentsTable: async () => {
    try {
      const response = await api.get(`/api/clinics/check-documents-table`);
      return response.data;
    } catch (error) {
      console.error('Error checking documents table:', error);
      return { exists: false, error: error.message };
    }
  },

  // Get clinic subscriptions
  getClinicSubscriptions: async (clinicId, tag = null, excludeTag = null) => {
    try {
      const params = {};
      if (tag) params.tag = tag;
      if (excludeTag) params.exclude_tag = excludeTag;

      const response = await api.get(`/api/clinics/${clinicId}/subscriptions`, { params });
      return response.data;
    } catch (error) {
      console.error(`Error fetching subscriptions for clinic ${clinicId}:`, error);
      // Return empty subscriptions array on error to prevent UI crashes
      return { subscriptions: [] };
    }
  },

  // Get subscription tag options for a clinic
  getSubscriptionTagOptions: async (clinicId) => {
    try {
      const response = await api.get(`/api/clinics/${clinicId}/subscriptions/tag-options`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching tag options for clinic ${clinicId}:`, error);
      // Return default tag options on error
      return {
        tagOptions: [
          { value: "emergency only", label: "Emergency Only" },
          { value: "testing", label: "Testing" },
          { value: "portal_only", label: "Portal Only" }
        ]
      };
    }
  },

  // Add a subscription to a clinic
  addClinicSubscription: async (clinicId, subscriptionData) => {
    try {
      // Ensure tags property is an array
      const data = {
        ...subscriptionData,
        tags: Array.isArray(subscriptionData.tags) ? subscriptionData.tags : []
      };
      const response = await api.post(`/api/clinics/${clinicId}/subscriptions`, data);
      return response.data;
    } catch (error) {
      console.error(`Error adding subscription to clinic ${clinicId}:`, error);
      throw error;
    }
  },

  // Update a clinic subscription
  updateClinicSubscription: async (clinicId, subscriptionId, subscriptionData) => {
    try {
      // Ensure tags property is an array
      const data = {
        ...subscriptionData,
        tags: Array.isArray(subscriptionData.tags) ? subscriptionData.tags : []
      };
      const response = await api.put(`/api/clinics/${clinicId}/subscriptions/${subscriptionId}`, data);
      return response.data;
    } catch (error) {
      console.error(`Error updating subscription ${subscriptionId} for clinic ${clinicId}:`, error);
      throw error;
    }
  },

  // Disable a clinic subscription (soft delete)
  deleteClinicSubscription: async (clinicId, subscriptionId) => {
    try {
      const response = await api.delete(`/api/clinics/${clinicId}/subscriptions/${subscriptionId}`);
      return response.data;
    } catch (error) {
      console.error(`Error disabling subscription ${subscriptionId} for clinic ${clinicId}:`, error);
      throw error;
    }
  },

  checkVoiceAsset: async (clinicId, assetType) => {
    try {
      const response = await api.get(`/api/clinics/${clinicId}/asset/${assetType}`);
      return response.data;
    } catch (error) {
      console.error(`Error checking voice asset for clinic ${clinicId}:`, error);
      return {
        exists: false,
        error: error.message
      };
    }
  },

  uploadVoiceAsset: async (clinicId, assetType, file) => {
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await api.post(`/api/clinics/${clinicId}/asset/${assetType}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } catch (error) {
      console.error(`Error uploading voice asset for clinic ${clinicId}:`, error);
      throw error;
    }
  },

  // Check if greeting voice asset exists
  checkGreetingVoiceAsset: async (clinicId) => {
    try {
      const response = await api.get(`/api/clinics/${clinicId}/asset/greeting_voice`);
      return response.data;
    } catch (error) {
      console.error(`Error checking greeting voice asset for clinic ${clinicId}:`, error);
      return {
        exists: false,
        error: error.message
      };
    }
  },

  // Upload greeting voice asset
  uploadGreetingVoiceAsset: async (clinicId, file) => {
    try {
      // Create form data
      const formData = new FormData();
      formData.append('file', file);

      const response = await api.post(`/api/clinics/${clinicId}/asset/greeting_voice`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } catch (error) {
      console.error(`Error uploading greeting voice asset for clinic ${clinicId}:`, error);
      throw error;
    }
  },

  checkVoiceMailGreetingAsset: async (clinicId) => {
    try {
      const response = await api.get(`/api/clinics/${clinicId}/asset/voice_mail_voice`);
      return response.data;
    } catch (error) {
      console.error(`Error checking voice mail greeting asset for clinic ${clinicId}:`, error);
      return {
        exists: false,
        error: error.message
      };
    }
  },

  uploadVoiceMailGreetingAsset: async (clinicId, file) => {
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await api.post(`/api/clinics/${clinicId}/asset/voice_mail_voice`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } catch (error) {
      console.error(`Error uploading voice mail greeting asset for clinic ${clinicId}:`, error);
      throw error;
    }
  },

  // Extract documents for a clinic
  extractDocuments: async (clinicId) => {
    try {
      const response = await api.post(`/api/clinics/${clinicId}/extract-documents`);
      return response.data;
    } catch (error) {
      console.error(`Error extracting documents for clinic ${clinicId}:`, error);
      throw error;
    }
  },

  // API Key Management Functions

  // Create a new API key for a clinic
  createApiKey: async (clinicId, apiKeyData) => {
    try {
      const response = await api.post(`/api/clinics/${clinicId}/api-keys`, apiKeyData);
      return response.data;
    } catch (error) {
      console.error(`Error creating API key for clinic ${clinicId}:`, error);
      throw error;
    }
  },

  // Get all API keys for a clinic
  getApiKeys: async (clinicId, activeOnly = true) => {
    try {
      const response = await api.get(`/api/clinics/${clinicId}/api-keys`, {
        params: { active_only: activeOnly }
      });
      return response.data;
    } catch (error) {
      console.error(`Error fetching API keys for clinic ${clinicId}:`, error);
      throw error;
    }
  },

  // Get a specific API key
  getApiKey: async (clinicId, apiKeyId, includePrivateKey = false) => {
    try {
      const response = await api.get(`/api/clinics/${clinicId}/api-keys/${apiKeyId}`, {
        params: { include_private_key: includePrivateKey }
      });
      return response.data;
    } catch (error) {
      console.error(`Error fetching API key ${apiKeyId} for clinic ${clinicId}:`, error);
      throw error;
    }
  },

  // Update an API key
  updateApiKey: async (clinicId, apiKeyId, apiKeyData) => {
    try {
      const response = await api.put(`/api/clinics/${clinicId}/api-keys/${apiKeyId}`, apiKeyData);
      return response.data;
    } catch (error) {
      console.error(`Error updating API key ${apiKeyId} for clinic ${clinicId}:`, error);
      throw error;
    }
  },

  // Delete an API key
  deleteApiKey: async (clinicId, apiKeyId) => {
    try {
      const response = await api.delete(`/api/clinics/${clinicId}/api-keys/${apiKeyId}`);
      return response.data;
    } catch (error) {
      console.error(`Error deleting API key ${apiKeyId} for clinic ${clinicId}:`, error);
      throw error;
    }
  },

  // Deactivate an API key (safer than deletion)
  deactivateApiKey: async (clinicId, apiKeyId) => {
    try {
      const response = await api.post(`/api/clinics/${clinicId}/api-keys/${apiKeyId}/deactivate`);
      return response.data;
    } catch (error) {
      console.error(`Error deactivating API key ${apiKeyId} for clinic ${clinicId}:`, error);
      throw error;
    }
  },

  // Test an API key
  testApiKey: async (clinicId, apiKeyId, testData) => {
    try {
      const response = await api.post(`/api/clinics/${clinicId}/api-keys/${apiKeyId}/test`, testData);
      return response.data;
    } catch (error) {
      console.error(`Error testing API key ${apiKeyId} for clinic ${clinicId}:`, error);
      throw error;
    }
  },

  // Notify clinic by human through conversation
  notifyClinicByHuman: async (conversationId, message) => {
    try {
      const response = await api.post(`/api/conversations/${conversationId}/notify_clinic_by_human`, { message_body: message });
      return response.data;
    } catch (error) {
      console.error(`Error notifying clinic for conversation ${conversationId}:`, error);
      throw error;
    }
  },

  // Get appointments for a conversation
  getConversationAppointments: async (conversationId) => {
    try {
      const response = await api.get(`/api/conversations/${conversationId}/appointments`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching appointments for conversation ${conversationId}:`, error);
      // Return empty appointments array on error to prevent UI crashes
      return { appointments: [] };
    }
  },

  // Get conversation events
  getConversationEvents: async (conversationId) => {
    try {
      const response = await api.get(`/api/conversations/${conversationId}/events`);
      return response.data;
    } catch (error) {
      console.error('Error fetching conversation events:', error);
      throw error;
    }
  },

  // Get block configuration for a clinic
  getClinicBlockConfig: async (clinicId, futureOnly = false) => {
    try {
      const response = await api.get(`/api/clinics/${clinicId}/block-config`, {
        params: { future_only: futureOnly }
      });
      return response.data;
    } catch (error) {
      console.error(`Error fetching block config for clinic ${clinicId}:`, error);
      throw error;
    }
  },

  // Get doctors for a clinic
  getClinicDoctors: async (clinicId) => {
    try {
      const response = await api.get(`/api/clinics/${clinicId}/doctors`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching doctors for clinic ${clinicId}:`, error);
      // Return empty doctors array on error to prevent UI crashes
      return { doctors: [], total: 0 };
    }
  },

  // Get all locations for a clinic
  getClinicLocations: async (clinicId, skip = 0, limit = 100, search = null) => {
    try {
      const params = { skip, limit };
      if (search) params.search = search;
      const response = await api.get(`/api/clinics/${clinicId}/locations`, { params });
      return response.data;
    } catch (error) {
      console.error(`Error fetching locations for clinic ${clinicId}:`, error);
      return { locations: [], total: 0 };
    }
  },

  // Create a new location
  createClinicLocation: async (clinicId, locationData) => {
    try {
      const response = await api.post(`/api/clinics/${clinicId}/locations`, locationData);
      return response.data;
    } catch (error) {
      console.error(`Error creating location for clinic ${clinicId}:`, error);
      throw error;
    }
  },

  // Update an existing location
  updateClinicLocation: async (clinicId, locationId, locationData) => {
    try {
      const response = await api.put(`/api/clinics/${clinicId}/locations/${locationId}`, locationData);
      return response.data;
    } catch (error) {
      console.error(`Error updating location ${locationId} for clinic ${clinicId}:`, error);
      throw error;
    }
  },

  // Delete a location
  deleteClinicLocation: async (clinicId, locationId) => {
    try {
      const response = await api.delete(`/api/clinics/${clinicId}/locations/${locationId}`);
      return response.data;
    } catch (error) {
      console.error(`Error deleting location ${locationId} for clinic ${clinicId}:`, error);
      throw error;
    }
  },

  // Add a doctor to a clinic
  addClinicDoctor: async (clinicId, doctorData) => {
    try {
      const response = await api.post(`/api/clinics/${clinicId}/doctors`, doctorData);
      return response.data;
    } catch (error) {
      console.error(`Error adding doctor to clinic ${clinicId}:`, error);
      throw error;
    }
  },

  // Update a clinic doctor
  updateClinicDoctor: async (clinicId, doctorId, doctorData) => {
    try {
      const response = await api.put(`/api/clinics/${clinicId}/doctors/${doctorId}`, doctorData);
      return response.data;
    } catch (error) {
      console.error(`Error updating doctor ${doctorId} for clinic ${clinicId}:`, error);
      throw error;
    }
  },

  // Delete a clinic doctor
  deleteClinicDoctor: async (clinicId, doctorId) => {
    try {
      const response = await api.delete(`/api/clinics/${clinicId}/doctors/${doctorId}`);
      return response.data;
    } catch (error) {
      console.error(`Error deleting doctor ${doctorId} from clinic ${clinicId}:`, error);
      throw error;
    }
  },

  // Apply holidays for the given year to clinic block configuration
  applyHolidays: async (clinicId, doctorId = null, year = 2027) => {
    try {
      const response = await api.post(`/api/clinics/${clinicId}/block-config/apply-holidays`, {
        doctor_id: doctorId,
        year: year
      });
      return response.data;
    } catch (error) {
      console.error(`Error applying holidays to clinic ${clinicId}:`, error);
      throw error;
    }
  },

  // Add a new block to clinic configuration
  addClinicBlock: async (clinicId, blockData) => {
    try {
      const timezoneOffset = new Date().getTimezoneOffset() / 60;
      if (blockData.block_from) {
        blockData.block_from = dayjs(blockData.block_from).utc().format();
      }
      if (blockData.block_until) {
        blockData.block_until = dayjs(blockData.block_until).utc().format();
      }
      // Ensure dates are in UTC format without changing the time
      const sanitizedBlockData = {
        ...blockData,
      };
      const response = await api.post(`/api/clinics/${clinicId}/block-config/blocks`, sanitizedBlockData);
      return response.data;
    } catch (error) {
      console.error(`Error adding block to clinic ${clinicId}:`, error);
      throw error;
    }
  },

  // Update an existing block in clinic configuration
  updateClinicBlock: async (clinicId, blockId, blockData) => {
    try {
      const response = await api.put(`/api/clinics/${clinicId}/block-config/blocks/${blockId}`, blockData);
      return response.data;
    } catch (error) {
      console.error(`Error updating block ${blockId} for clinic ${clinicId}:`, error);
      throw error;
    }
  },

  // Delete a block from clinic configuration
  deleteClinicBlock: async (clinicId, blockId) => {
    try {
      const response = await api.delete(`/api/clinics/${clinicId}/block-config/blocks/${blockId}`);
      return response.data;
    } catch (error) {
      console.error(`Error deleting block ${blockId} from clinic ${clinicId}:`, error);
      throw error;
    }
  },

  // Get a specific block from clinic configuration
  getClinicBlock: async (clinicId, blockId) => {
    try {
      const response = await api.get(`/api/clinics/${clinicId}/block-config/blocks/${blockId}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching block ${blockId} for clinic ${clinicId}:`, error);
      throw error;
    }
  },

  // Get special notices for a clinic
  getClinicSpecialNotices: async (clinicId, futureOnly = false) => {
    try {
      const response = await api.get(`/api/clinics/${clinicId}/special-notices`, {
        params: { future_only: futureOnly }
      });
      return response.data;
    } catch (error) {
      console.error(`Error fetching special notices for clinic ${clinicId}:`, error);
      throw error;
    }
  },

  // Add a new special notice
  addClinicSpecialNotice: async (clinicId, noticeData) => {
    try {
      const response = await api.post(`/api/clinics/${clinicId}/special-notices`, noticeData);
      return response.data;
    } catch (error) {
      console.error(`Error adding special notice to clinic ${clinicId}:`, error);
      throw error;
    }
  },

  // Update an existing special notice
  updateClinicSpecialNotice: async (clinicId, noticeId, noticeData) => {
    try {
      const response = await api.put(`/api/clinics/${clinicId}/special-notices/${noticeId}`, noticeData);
      return response.data;
    } catch (error) {
      console.error(`Error updating special notice ${noticeId} for clinic ${clinicId}:`, error);
      throw error;
    }
  },

  // Delete a special notice
  deleteClinicSpecialNotice: async (clinicId, noticeId) => {
    try {
      const response = await api.delete(`/api/clinics/${clinicId}/special-notices/${noticeId}`);
      return response.data;
    } catch (error) {
      console.error(`Error deleting special notice ${noticeId} from clinic ${clinicId}:`, error);
      throw error;
    }
  },

  // Clinic configuration entities (entity / branch / appointment_type)
  getClinicEntities: async (clinicId, type = null, includeInactive = false) => {
    try {
      const params = {};
      if (type) params.type = type;
      if (includeInactive) params.include_inactive = true;
      const response = await api.get(`/api/clinics/${clinicId}/entities`, { params });
      return response.data;
    } catch (error) {
      console.error(`Error fetching entities for clinic ${clinicId}:`, error);
      throw error;
    }
  },

  getClinicEntity: async (clinicId, entityId) => {
    try {
      const response = await api.get(`/api/clinics/${clinicId}/entities/${entityId}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching entity ${entityId} for clinic ${clinicId}:`, error);
      throw error;
    }
  },

  createClinicEntity: async (clinicId, data) => {
    try {
      const response = await api.post(`/api/clinics/${clinicId}/entities`, data);
      return response.data;
    } catch (error) {
      console.error(`Error creating entity for clinic ${clinicId}:`, error);
      throw error;
    }
  },

  updateClinicEntity: async (clinicId, entityId, data) => {
    try {
      const response = await api.put(`/api/clinics/${clinicId}/entities/${entityId}`, data);
      return response.data;
    } catch (error) {
      console.error(`Error updating entity ${entityId} for clinic ${clinicId}:`, error);
      throw error;
    }
  },

  getEntityMetaDataSchemas: async () => {
    try {
      const response = await api.get('/api/clinics/entity-meta-data-schemas');
      return response.data;
    } catch (error) {
      console.error('Error fetching entity meta_data schemas:', error);
      throw error;
    }
  },

  // QA Knowledge Base
  getClinicQAItems: async (clinicId, skip = 0, limit = 300, isActive = null) => {
    try {
      let params = { skip, limit };
      if (isActive !== null) {
        params.is_active = isActive;
      }
      const response = await api.get(`/api/clinics/${clinicId}/qa`, { params });
      return response.data;
    } catch (error) {
      console.error(`Error fetching QA items for clinic ${clinicId}:`, error);
      throw error;
    }
  },

  searchClinicQAItems: async (clinicId, search, skip = 0, limit = 300, isActive = null) => {
    try {
      let params = { skip, limit, search };
      if (isActive !== null) {
        params.is_active = isActive;
      }
      const response = await api.get(`/api/clinics/${clinicId}/qa`, { params });
      return response.data;
    } catch (error) {
      console.error(`Error searching QA items for clinic ${clinicId}:`, error);
      throw error;
    }
  },

  getQAItemById: async (clinicId, qaId) => {
    try {
      const response = await api.get(`/api/clinics/${clinicId}/qa/${qaId}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching QA item ${qaId} for clinic ${clinicId}:`, error);
      throw error;
    }
  },

  createQAItem: async (clinicId, qaItem) => {
    try {
      const response = await api.post(`/api/clinics/${clinicId}/qa`, qaItem);
      return response.data;
    } catch (error) {
      console.error(`Error creating QA item for clinic ${clinicId}:`, error);
      throw error;
    }
  },

  importQAItems: async (clinicId, qaItems, attachRule = 'always') => {
    try {
      const response = await api.post(`/api/clinics/${clinicId}/qa/import`, qaItems, {
        params: { attach_rule: attachRule }
      });
      return response.data;
    } catch (error) {
      console.error(`Error importing QA items for clinic ${clinicId}:`, error);
      throw error;
    }
  },

  updateQAItem: async (clinicId, qaId, qaItemData) => {
    try {
      const response = await api.put(`/api/clinics/${clinicId}/qa/${qaId}`, qaItemData);
      return response.data;
    } catch (error) {
      console.error(`Error updating QA item ${qaId} for clinic ${clinicId}:`, error);
      throw error;
    }
  },

  deleteQAItem: async (clinicId, qaId) => {
    try {
      const response = await api.delete(`/api/clinics/${clinicId}/qa/${qaId}`);
      return response.data;
    } catch (error) {
      console.error(`Error deleting QA item ${qaId} for clinic ${clinicId}:`, error);
      throw error;
    }
  },

  // Export QA items as simplified JSON
  exportQAItems: async (clinicId, { tagId = null, isActive = true } = {}) => {
    try {
      const params = {};
      if (tagId) params.tag_id = tagId;
      if (typeof isActive === 'boolean') params.is_active = isActive;
      const response = await api.get(`/api/clinics/${clinicId}/qa/export`, {
        params,
      });
      return response.data;
    } catch (error) {
      console.error(`Error exporting QA items for clinic ${clinicId}:`, error);
      throw error;
    }
  },

  // Export QA items as HTML table
  exportQAItemsToHTML: async (clinicId, { tagId = null, isActive = true } = {}) => {
    try {
      const params = {};
      if (tagId) params.tag_id = tagId;
      if (typeof isActive === 'boolean') params.is_active = isActive;
      const response = await api.get(`/api/clinics/${clinicId}/qa/export/html`, {
        params,
        responseType: 'blob', // Important for downloading files
      });
      return response.data;
    } catch (error) {
      console.error(`Error exporting QA items to HTML for clinic ${clinicId}:`, error);
      throw error;
    }
  },

  // QA Tags
  getClinicQATags: async (clinicId) => {
    try {
      const response = await api.get(`/api/clinics/${clinicId}/qa-tags`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching QA tags for clinic ${clinicId}:`, error);
      throw error;
    }
  },

  createQATag: async (clinicId, tag) => {
    try {
      const response = await api.post(`/api/clinics/${clinicId}/qa-tags`, tag);
      return response.data;
    } catch (error) {
      console.error(`Error creating QA tag for clinic ${clinicId}:`, error);
      throw error;
    }
  },

  updateQATag: async (clinicId, tagId, tag) => {
    try {
      const response = await api.put(`/api/clinics/${clinicId}/qa-tags/${tagId}`, tag);
      return response.data;
    } catch (error) {
      console.error(`Error updating QA tag ${tagId} for clinic ${clinicId}:`, error);
      throw error;
    }
  },

  deleteQATag: async (clinicId, tagId) => {
    try {
      const response = await api.delete(`/api/clinics/${clinicId}/qa-tags/${tagId}`);
      return response.data;
    } catch (error) {
      console.error(`Error deleting QA tag ${tagId} for clinic ${clinicId}:`, error);
      throw error;
    }
  },

  // Vector Query Test
  testVectorQuery: async (clinicId, query, k = 5) => {
    try {
      const response = await api.post(`/api/clinics/${clinicId}/qa/vector-test`, {
        query,
        k
      });
      return response.data;
    } catch (error) {
      console.error(`Error testing vector query for clinic ${clinicId}:`, error);
      throw error;
    }
  },

  // Clinic hint summary (markdown)
  getClinicSummary: async (clinicId) => {
    try {
      const response = await api.get(`/api/clinics/${clinicId}/summary`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching clinic summary for clinic ${clinicId}:`, error);
      throw error;
    }
  },

  saveClinicSummary: async (clinicId, summaryData) => {
    try {
      const response = await api.put(`/api/clinics/${clinicId}/summary`, summaryData);
      return response.data;
    } catch (error) {
      console.error(`Error saving clinic summary for clinic ${clinicId}:`, error);
      throw error;
    }
  },

  // Clinic quota
  getClinicQuotaSummary: async (clinicId) => {
    try {
      const response = await api.get(`/api/clinics/${clinicId}/quota`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching quota summary for clinic ${clinicId}:`, error);
      throw error;
    }
  },

  getClinicQuotaPurchases: async (clinicId, limit = 50, offset = 0) => {
    try {
      const response = await api.get(`/api/clinics/${clinicId}/quota/purchases`, {
        params: { limit, offset },
      });
      return response.data;
    } catch (error) {
      console.error(`Error fetching quota purchases for clinic ${clinicId}:`, error);
      throw error;
    }
  },

  getClinicQuotaAdjustments: async (clinicId, limit = 100, offset = 0) => {
    try {
      const response = await api.get(`/api/clinics/${clinicId}/quota/adjustments`, {
        params: { limit, offset },
      });
      return response.data;
    } catch (error) {
      console.error(`Error fetching quota adjustments for clinic ${clinicId}:`, error);
      throw error;
    }
  },

  adjustClinicQuota: async (clinicId, payload) => {
    try {
      const response = await api.post(`/api/clinics/${clinicId}/quota/adjust`, payload);
      return response.data;
    } catch (error) {
      console.error(`Error adjusting quota for clinic ${clinicId}:`, error);
      throw error;
    }
  },

  // Get doctor schedules
  getDoctorSchedules: async (clinicId, doctorId) => {
    try {
      const response = await api.get(`/api/clinics/${clinicId}/doctors/${doctorId}/schedules`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching schedules for doctor ${doctorId} in clinic ${clinicId}:`, error);
      return { schedules: [] };
    }
  },

  // Export all doctor schedules for a clinic as one JSON document
  exportDoctorSchedulesJson: async (clinicId) => {
    const response = await api.get(`/api/clinics/${clinicId}/doctor-schedules/export`);
    return response.data;
  },

  // Import/configure all doctor schedules from JSON
  importDoctorSchedulesJson: async (clinicId, payload) => {
    const response = await api.post(`/api/clinics/${clinicId}/doctor-schedules/import`, payload);
    return response.data;
  },

  // Fetch schedules expiring within the next `days` days (default 30)
  getExpiringSchedules: async (days = 30) => {
    const response = await api.get(`/api/clinics/doctor-schedules/expiring-soon`, { params: { days } });
    return response.data;
  },

  // Create a new doctor schedule
  createDoctorSchedule: async (clinicId, doctorId, scheduleData) => {
    try {
      const response = await api.post(
        `/api/clinics/${clinicId}/doctors/${doctorId}/schedules`,
        scheduleData
      );
      return response.data;
    } catch (error) {
      console.error(`Error creating schedule for doctor ${doctorId} in clinic ${clinicId}:`, error);
      throw error;
    }
  },

  // Update an existing doctor schedule
  updateDoctorSchedule: async (clinicId, doctorId, scheduleData) => {
    try {
      if (!scheduleData.id) {
        throw new Error('Schedule ID is required for updates');
      }

      const response = await api.put(
        `/api/clinics/${clinicId}/doctors/${doctorId}/schedules/${scheduleData.id}`,
        scheduleData
      );
      return response.data;
    } catch (error) {
      console.error(`Error updating schedule for doctor ${doctorId} in clinic ${clinicId}:`, error);
      throw error;
    }
  },

  // Delete doctor schedule
  deleteDoctorSchedule: async (clinicId, doctorId, scheduleId) => {
    try {
      const response = await api.delete(`/api/clinics/${clinicId}/doctors/${doctorId}/schedules/${scheduleId}`);
      return response.data;
    } catch (error) {
      console.error(`Error deleting schedule ${scheduleId} for doctor ${doctorId} in clinic ${clinicId}:`, error);
      throw error;
    }
  },

  // Appointment Reminder Settings

  // Get all appointment reminder settings for a clinic
  getAppointmentReminderSettings: async (clinicId, filters = {}) => {
    try {
      const params = {
        skip: filters.skip || 0,
        limit: filters.limit || 100
      };
      if (filters.location_id !== undefined) params.location_id = filters.location_id;
      if (filters.doctor_id !== undefined) params.doctor_id = filters.doctor_id;
      if (filters.enabled !== undefined) params.enabled = filters.enabled;

      const response = await api.get(`/api/clinics/${clinicId}/appointment-reminder-settings`, { params });
      return response.data;
    } catch (error) {
      console.error(`Error fetching appointment reminder settings for clinic ${clinicId}:`, error);
      throw error;
    }
  },

  // Get a specific appointment reminder setting
  getAppointmentReminderSetting: async (clinicId, settingId) => {
    try {
      const response = await api.get(`/api/clinics/${clinicId}/appointment-reminder-settings/${settingId}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching appointment reminder setting ${settingId} for clinic ${clinicId}:`, error);
      throw error;
    }
  },

  // Create a new appointment reminder setting
  createAppointmentReminderSetting: async (clinicId, settingData) => {
    try {
      const response = await api.post(`/api/clinics/${clinicId}/appointment-reminder-settings`, settingData);
      return response.data;
    } catch (error) {
      console.error(`Error creating appointment reminder setting for clinic ${clinicId}:`, error);
      throw error;
    }
  },

  // Update an existing appointment reminder setting
  updateAppointmentReminderSetting: async (clinicId, settingId, settingData) => {
    try {
      const response = await api.put(`/api/clinics/${clinicId}/appointment-reminder-settings/${settingId}`, settingData);
      return response.data;
    } catch (error) {
      console.error(`Error updating appointment reminder setting ${settingId} for clinic ${clinicId}:`, error);
      throw error;
    }
  },

  // Delete an appointment reminder setting
  deleteAppointmentReminderSetting: async (clinicId, settingId) => {
    try {
      const response = await api.delete(`/api/clinics/${clinicId}/appointment-reminder-settings/${settingId}`);
      return response.data;
    } catch (error) {
      console.error(`Error deleting appointment reminder setting ${settingId} for clinic ${clinicId}:`, error);
      throw error;
    }
  },

  // Toggle enabled status of an appointment reminder setting
  toggleAppointmentReminderSettingEnabled: async (clinicId, settingId) => {
    try {
      const response = await api.post(`/api/clinics/${clinicId}/appointment-reminder-settings/${settingId}/toggle-enabled`);
      return response.data;
    } catch (error) {
      console.error(`Error toggling appointment reminder setting ${settingId} for clinic ${clinicId}:`, error);
      throw error;
    }
  },

  // Export clinic configuration to JSON
  exportClinicConfiguration: async (clinicId) => {
    try {
      const response = await api.get(`/api/clinics/${clinicId}/export`);
      return response.data;
    } catch (error) {
      console.error(`Error exporting clinic ${clinicId} configuration:`, error);
      throw error;
    }
  },

  // Import clinic configuration from JSON to create a new clinic
  importClinicConfiguration: async (importData) => {
    try {
      const response = await api.post(`/api/clinics/import`, importData);
      return response.data;
    } catch (error) {
      console.error(`Error importing clinic configuration:`, error);
      throw error;
    }
  },

  // Update the current clinic in place from full clinic-configuration JSON
  updateClinicConfigurationFromJson: async (clinicId, importData) => {
    const response = await api.post(`/api/clinics/${clinicId}/import`, importData);
    return response.data;
  },

  // Media Management

  // Get all media for a clinic
  getClinicMedia: async (clinicId, skip = 0, limit = 100, search = null, isActive = null, mediaCategory = null) => {
    try {
      const params = { skip, limit };
      if (search) params.search = search;
      if (isActive !== null) params.is_active = isActive;
      if (mediaCategory) params.media_category = mediaCategory;

      const response = await api.get(`/api/clinics/${clinicId}/media`, { params });
      return response.data;
    } catch (error) {
      console.error(`Error fetching media for clinic ${clinicId}:`, error);
      // Return empty media array on error to prevent UI crashes
      return { items: [], total: 0 };
    }
  },

  // Get a single media entry
  getMedia: async (clinicId, mediaId) => {
    try {
      const response = await api.get(`/api/clinics/${clinicId}/media/${mediaId}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching media ${mediaId} for clinic ${clinicId}:`, error);
      throw error;
    }
  },

  // Upload a media file
  uploadMedia: async (clinicId, file, mediaData) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('media_name', mediaData.media_name);
      formData.append('media_description', mediaData.media_description || '');
      formData.append('media_tags', mediaData.media_tags || '');
      formData.append('media_category', mediaData.media_category || 'general');

      const response = await api.post(`/api/clinics/${clinicId}/media/upload`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } catch (error) {
      console.error(`Error uploading media for clinic ${clinicId}:`, error);
      throw error;
    }
  },

  // Create a new media entry (without file upload)
  createMedia: async (clinicId, mediaData) => {
    try {
      const response = await api.post(`/api/clinics/${clinicId}/media`, mediaData);
      return response.data;
    } catch (error) {
      console.error(`Error creating media for clinic ${clinicId}:`, error);
      throw error;
    }
  },

  // Update a media entry
  updateMedia: async (clinicId, mediaId, mediaData) => {
    try {
      const response = await api.put(`/api/clinics/${clinicId}/media/${mediaId}`, mediaData);
      return response.data;
    } catch (error) {
      console.error(`Error updating media ${mediaId} for clinic ${clinicId}:`, error);
      throw error;
    }
  },

  // Delete a media entry
  deleteMedia: async (clinicId, mediaId) => {
    try {
      const response = await api.delete(`/api/clinics/${clinicId}/media/${mediaId}`);
      return response.data;
    } catch (error) {
      console.error(`Error deleting media ${mediaId} for clinic ${clinicId}:`, error);
      throw error;
    }
  },

  getClinicTemplates: async (clinicId) => {
    try {
      const response = await api.get(`/api/clinics/${clinicId}/templates`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching templates for clinic ${clinicId}:`, error);
      throw error;
    }
  },

  createClinicTemplate: async (clinicId, templateData) => {
    try {
      console.log(`Creating template for clinic ${clinicId}:`, templateData);
      const response = await api.post(`/api/clinics/${clinicId}/templates`, templateData);
      return response.data;
    } catch (error) {
      console.error(`Error creating template for clinic ${clinicId}:`, error);
      throw error;
    }
  },

  updateClinicTemplate: async (clinicId, templateId, templateData) => {
    try {
      console.log(`Updating template ${templateId} for clinic ${clinicId}:`, templateData);
      const response = await api.put(`/api/clinics/${clinicId}/templates/${templateId}`, templateData);
      return response.data;
    } catch (error) {
      console.error(`Error updating template ${templateId} for clinic ${clinicId}:`, error);
      throw error;
    }
  },

  deleteClinicTemplate: async (clinicId, templateId) => {
    try {
      console.log(`Deleting template ${templateId} for clinic ${clinicId}`);
      const response = await api.delete(`/api/clinics/${clinicId}/templates/${templateId}`);
      return response.data;
    } catch (error) {
      console.error(`Error deleting template ${templateId} for clinic ${clinicId}:`, error);
      throw error;
    }
  },

  getDefaultNamespace: async (clinicId) => {
    try {
      console.log(`Getting default namespace for clinic ${clinicId}`);
      const response = await api.get(`/api/clinics/${clinicId}/default-namespace`);
      return response.data;
    } catch (error) {
      console.error(`Error getting default namespace for clinic ${clinicId}:`, error);
      return { default_namespace: 'default', concierge_version: null };
    }
  },

};

export const conversationService = {
  // Send a message in a conversation
  sendMessage: async (conversationId, message) => {
    try {
      const response = await api.post(`/api/conversations/${conversationId}/send_message`, {
        message_body: message
      });
      return response.data;
    } catch (error) {
      console.error(`Error sending message in conversation ${conversationId}:`, error);
      throw error;
    }
  },

  // Send a message via outbound handler
  sendOutboundMessage: async (conversationId, message) => {
    try {
      const response = await api.post(`/api/conversations/${conversationId}/send_outbound_message`, {
        message_body: message
      });
      return response.data;
    } catch (error) {
      console.error(`Error sending outbound message in conversation ${conversationId}:`, error);
      throw error;
    }
  },

  sendTemplate: async (conversationId, templateId) => {
    try {
      const response = await api.post(`/api/conversations/${conversationId}/send_template`, {
        template_name: templateId
      });
      return response.data;
    } catch (error) {
      console.error(`Error sending template in conversation ${conversationId}:`, error);
      throw error;
    }
  },

  // Get WhatsApp flow data by ID
  getWhatsappFlowData: async (flowDataId) => {
    try {
      const response = await api.get(`/api/conversations/whatsapp-flow/${flowDataId}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching WhatsApp flow data ${flowDataId}:`, error);
      throw error;
    }
  },

  // Search conversations by phone number across all clinics
  searchByPhoneNumber: async (phoneNumber, page = 1, perPage = 20) => {
    try {
      const response = await api.get('/api/conversations/search-by-phone', {
        params: {
          phone_number: phoneNumber,
          page,
          per_page: perPage
        }
      });
      return response.data;
    } catch (error) {
      console.error(`Error searching conversations by phone number ${phoneNumber}:`, error);
      throw error;
    }
  }
};

export const conversationAnnotationService = {
  // Create a new annotation
  createAnnotation: async (annotationData) => {
    try {
      const response = await api.post('/api/conversation-annotations/', annotationData);
      return response.data;
    } catch (error) {
      console.error('Error creating annotation:', error);
      throw error;
    }
  },

  // Get annotations for a conversation
  getConversationAnnotations: async (conversationId) => {
    try {
      const response = await api.get(`/api/conversation-annotations/conversation/${conversationId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching conversation annotations:', error);
      throw error;
    }
  },

  // Get a specific annotation
  getAnnotation: async (annotationId) => {
    try {
      const response = await api.get(`/api/conversation-annotations/${annotationId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching annotation:', error);
      throw error;
    }
  },

  // Update an annotation
  updateAnnotation: async (annotationId, updateData) => {
    try {
      const response = await api.put(`/api/conversation-annotations/${annotationId}`, updateData);
      return response.data;
    } catch (error) {
      console.error('Error updating annotation:', error);
      throw error;
    }
  },

  // Delete an annotation
  deleteAnnotation: async (annotationId) => {
    try {
      const response = await api.delete(`/api/conversation-annotations/${annotationId}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting annotation:', error);
      throw error;
    }
  },

  // Get annotated messages for a conversation
  getAnnotatedMessages: async (conversationId) => {
    try {
      const response = await api.get(`/api/conversation-annotations/conversation/${conversationId}/annotated-messages`);
      return response.data;
    } catch (error) {
      console.error('Error fetching annotated messages:', error);
      throw error;
    }
  }
};

export const templateService = {
  // Get available templates
  getTemplates: async () => {
    return {
      templates: [
        { id: 'hello_world', content: 'Welcome and congratulations!! This message demonstrates your ability to send a WhatsApp message notification from the Cloud API, hosted by Meta. Thank you for taking the time to test with us.' },
        { id: 'Appointment Confirmation', content: 'Your appointment has been confirmed for {date} at {time}.' },
        { id: 'Appointment Reminder', content: 'Reminder: You have an appointment scheduled for {date} at {time}.' },
        { id: 'General Information', content: 'Thank you for contacting us. Our office hours are Monday-Friday, 9am-5pm.' },
        { id: 'Follow-up', content: 'Following up on your recent visit. How are you feeling?' }
      ]
    };
  },
};

// API functions for toolbox
export const toolboxService = {
  mediaBulkPreview: async (clid, zipFile, signal) => {
    try {
      const formData = new FormData();
      formData.append('clid', clid);
      formData.append('zip_file', zipFile);
      const response = await api.post('/api/toolbox/media-bulk/preview', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        ...(signal ? { signal } : {})
      });
      return response.data;
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || error.message || 'Failed to preview media bulk upload'
      };
    }
  },

  mediaBulkCommit: async (clid, zipFile, rows, signal) => {
    try {
      const formData = new FormData();
      formData.append('clid', clid);
      formData.append('rows_json', JSON.stringify(rows));
      formData.append('zip_file', zipFile);
      const response = await api.post('/api/toolbox/media-bulk/commit', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        ...(signal ? { signal } : {})
      });
      return response.data;
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || error.message || 'Failed to commit media bulk upload'
      };
    }
  },

  // Scrape doctor information from URL
  scrapeDoctorInfo: async (url, clid) => {
    try {
      const response = await api.post('/api/toolbox/doctor-schedule/scrape', {
        url,
        clid
      });
      return response.data;
    } catch (error) {
      console.error('Error scraping doctor info:', error);
      return {
        success: false,
        error: error.response?.data?.detail || error.message || 'Failed to scrape doctor information'
      };
    }
  },

  // Import doctors and schedules from JSON data
  importDoctorsAndSchedules: async (clid, doctors, signal) => {
    try {
      const response = await api.post('/api/toolbox/doctor-schedule/import', { clid, doctors }, { signal });
      return response.data;
    } catch (error) {
      console.error('Error importing doctors and schedules:', error);
      return {
        success: false,
        error: error.response?.data?.detail || error.message || 'Failed to import doctors and schedules'
      };
    }
  },

  // Scrape and generate QA from URL
  scrapeAndGenerateQA: async (url, signal) => {
    try {
      const response = await api.post(
        '/api/toolbox/qa-knowledge-base/scrape',
        { url },
        { signal }
      );
      return response.data;
    } catch (error) {
      console.error('Error scraping and generating QA:', error);
      return {
        success: false,
        error: error.response?.data?.detail || error.message || 'Failed to scrape and generate QA'
      };
    }
  },
  generatePricingFromImageUrl: async (url, signal) => {
    try {
      const response = await api.post(
        '/api/toolbox/qa-knowledge-base/pricing-list/url',
        { url },
        { signal }
      );
      return response.data;
    } catch (error) {
      console.error('Error generating pricing list from URL:', error);
      return {
        success: false,
        error: error.response?.data?.detail || error.message || 'Failed to generate pricing list from URL'
      };
    }
  },
  generatePricingFromImageUpload: async (file, signal) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await api.post('/api/toolbox/qa-knowledge-base/pricing-list/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        signal
      });
      return response.data;
    } catch (error) {
      console.error('Error generating pricing list from uploaded image:', error);
      return {
        success: false,
        error: error.response?.data?.detail || error.message || 'Failed to generate pricing list from uploaded image'
      };
    }
  },

  flexibleExtractScrapeOnly: async (url, signal) => {
    try {
      const response = await api.post(
        '/api/toolbox/flexible-extract/scrape-only',
        { url },
        signal ? { signal } : {}
      );
      return response.data;
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || error.message || 'Failed to fetch'
      };
    }
  },

  flexibleExtractAnalyze: async (url, content, instruction, fields, signal) => {
    try {
      const response = await api.post(
        '/api/toolbox/flexible-extract/analyze',
        { url, content, instruction, fields },
        signal ? { signal } : {}
      );
      return response.data;
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || error.message || 'Failed to analyze'
      };
    }
  },

  flexibleExtractAnalyzeGrouped: async (items, instruction, fields, signal) => {
    try {
      const response = await api.post(
        '/api/toolbox/flexible-extract/analyze-grouped',
        { items, instruction, fields },
        signal ? { signal } : {}
      );
      return response.data;
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || error.message || 'Failed to analyze'
      };
    }
  },

  flexibleExtractScrape: async (url, instruction, fields, signal) => {
    try {
      const response = await api.post(
        '/api/toolbox/flexible-extract/scrape',
        { url, instruction, fields },
        signal ? { signal } : {}
      );
      return response.data;
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || error.message || 'Failed to extract'
      };
    }
  },

  // Get FindDoc Agent analytics summary (full result, proxied to nexi-router)
  getFinddocAnalyticsSummary: async (params = {}) => {
    try {
      const { clid, start_date, end_date } = params;
      const response = await api.get('/api/toolbox/finddoc-analytics/summary', {
        params: {
          ...(clid && { clid }),
          ...(start_date && { start_date }),
          ...(end_date && { end_date })
        }
      });
      return response.data;
    } catch (error) {
      const detail = error.response?.data?.detail;
      const message = typeof detail === 'string' ? detail : (detail?.msg || error.message);
      return {
        success: false,
        error: message || 'Failed to fetch FindDoc analytics summary'
      };
    }
  },

  // List conversation messages with filtering, grouping, sorting, and pagination
  listConversationMessages: async (params = {}) => {
    try {
      const { clid, group_by_conversation, page = 1, per_page = 50 } = params;
      const response = await api.get('/api/toolbox/conversation-messages', {
        params: {
          ...(clid && { clid }),
          ...(group_by_conversation !== undefined && { group_by_conversation }),
          page,
          per_page
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error listing conversation messages:', error);
      return {
        success: false,
        error: error.response?.data?.detail || error.message || 'Failed to list conversation messages'
      };
    }
  },

  // Delete a conversation message by ID
  deleteConversationMessage: async (messageId) => {
    try {
      const response = await api.delete(`/api/toolbox/conversation-messages/${messageId}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting conversation message:', error);
      return {
        success: false,
        error: error.response?.data?.detail || error.message || 'Failed to delete conversation message'
      };
    }
  },

  // Import conversation messages from CSV (local development only)
  importConversationMessagesCsv: async (file) => {
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await api.post('/api/toolbox/conversation-messages/import', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      return response.data;
    } catch (error) {
      console.error('Error importing conversation messages from CSV:', error);
      return {
        success: false,
        error: error.response?.data?.detail || error.message || 'Failed to import conversation messages from CSV',
      };
    }
  },

  // Import conversation state events from CSV (local development only)
  importConversationStateEventsCsv: async (file) => {
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await api.post('/api/toolbox/conversation-state-events/import', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      return response.data;
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || error.message || 'Failed to import conversation state events from CSV',
      };
    }
  },

  /**
   * mode: 'month' -> pass yearMonth; 'range' -> pass startDate + endDate.
   */
  previewHKSHReport: async ({ mode = 'month', yearMonth, startDate, endDate, previewLimit = 500, clids } = {}) => {
    try {
      const dateFields =
        mode === 'range'
          ? { start_date: startDate, end_date: endDate }
          : { year_month: yearMonth };
      const response = await api.post('/api/toolbox/hksh-report/preview', {
        ...dateFields,
        preview_limit: previewLimit,
        ...(clids != null && clids.length > 0 ? { clids } : {}),
      });
      return { success: true, data: response.data };
    } catch (error) {
      const detail = error.response?.data?.detail;
      const message = typeof detail === 'string' ? detail : error.message;
      return { success: false, error: message || 'Failed to generate HKSH report preview' };
    }
  },

  exportHKSHReportZip: async ({ mode = 'month', yearMonth, startDate, endDate, clids } = {}) => {
    try {
      const dateFields =
        mode === 'range'
          ? { start_date: startDate, end_date: endDate }
          : { year_month: yearMonth };
      const response = await api.post(
        '/api/toolbox/hksh-report/export',
        {
          ...dateFields,
          preview_limit: 500,
          ...(clids != null && clids.length > 0 ? { clids } : {}),
        },
        { responseType: 'blob' }
      );
      return { success: true, blob: response.data };
    } catch (error) {
      const detail = error.response?.data?.detail;
      const message = typeof detail === 'string' ? detail : error.message;
      return { success: false, error: message || 'Failed to export HKSH report ZIP' };
    }
  },

  /**
   * mode: 'month' -> pass yearMonth; 'range' -> pass startDate + endDate.
   * clid: CLID3596 (default / production) or DEMO0004 (testing).
   */
  previewOncoCareSurveyReport: async ({ mode = 'month', yearMonth, startDate, endDate, previewLimit = 500, clid = 'CLID3596' } = {}) => {
    try {
      const dateFields =
        mode === 'range'
          ? { start_date: startDate, end_date: endDate }
          : { year_month: yearMonth };
      const response = await api.post('/api/toolbox/oncocare-survey-report/preview', {
        ...dateFields,
        preview_limit: previewLimit,
        clid,
      });
      return { success: true, data: response.data };
    } catch (error) {
      const detail = error.response?.data?.detail;
      const message = typeof detail === 'string' ? detail : error.message;
      return { success: false, error: message || 'Failed to generate OncoCare survey preview' };
    }
  },

  exportOncoCareSurveyReportCsv: async ({ mode = 'month', yearMonth, startDate, endDate, clid = 'CLID3596' } = {}) => {
    try {
      const dateFields =
        mode === 'range'
          ? { start_date: startDate, end_date: endDate }
          : { year_month: yearMonth };
      const response = await api.post(
        '/api/toolbox/oncocare-survey-report/export',
        {
          ...dateFields,
          preview_limit: 500,
          clid,
        },
        { responseType: 'blob' }
      );
      return { success: true, blob: response.data };
    } catch (error) {
      const detail = error.response?.data?.detail;
      const message = typeof detail === 'string' ? detail : error.message;
      return { success: false, error: message || 'Failed to export OncoCare survey CSV' };
    }
  },
};

// API functions for testing
export const testingService = {
  // Test agent API (legacy direct prediction; prefer sendTestAgentMessage + poll)
  testAgentPrediction: async (requestData) => {
    try {
      const response = await api.post('/api/testing/agent/prediction', requestData);
      return response.data;
    } catch (error) {
      console.error('Error testing agent prediction:', error);
      return {
        success: false,
        error: error.response?.data?.detail || error.message || 'Failed to test agent prediction'
      };
    }
  },

  // Send test message via router testing inbound; then poll GET conversation-messages for reply
  sendTestAgentMessage: async (conversationId, clid, messageContent) => {
    try {
      const response = await api.post('/api/testing/agent/send', {
        conversation_id: conversationId,
        clid,
        message_content: messageContent,
        message_type: 'text'
      });
      return response.data;
    } catch (error) {
      console.error('Error sending test agent message:', error);
      return {
        success: false,
        error: error.response?.data?.detail || error.message || 'Failed to send message'
      };
    }
  },

  // List test conversations for a clinic (from ConversationMessage)
  getTestAgentConversations: async (clid) => {
    try {
      const response = await api.get('/api/testing/agent/conversations', {
        params: { clid }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching test agent conversations:', error);
      return { success: false, conversations: [], error: error.response?.data?.detail || error.message };
    }
  },

  // Save test conversation
  saveConversation: async (clinicId, sessionId, name, chatHistory) => {
    try {
      const response = await api.post('/api/testing/conversations/save', {
        clinic_id: clinicId,
        session_id: sessionId,
        name,
        chat_history: chatHistory
      });
      return response.data;
    } catch (error) {
      console.error('Error saving test conversation:', error);
      return {
        success: false,
        error: error.response?.data?.detail || error.message || 'Failed to save conversation'
      };
    }
  },

  // Get saved conversations
  getSavedConversations: async (clinicId) => {
    try {
      const response = await api.get(`/api/testing/conversations?clinic_id=${clinicId}`);
      return response.data;
    } catch (error) {
      console.error('Error getting saved conversations:', error);
      return {
        success: false,
        error: error.response?.data?.detail || error.message || 'Failed to get saved conversations'
      };
    }
  },

  // Get test agent chat history from ConversationMessage (source=testing)
  getTestAgentConversationMessages: async (conversationId) => {
    try {
      const response = await api.get('/api/testing/agent/conversation-messages', {
        params: { conversation_id: conversationId }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching test agent conversation messages:', error);
      return { success: false, messages: [], error: error.response?.data?.detail || error.message };
    }
  },

  // Save test agent chat history to ConversationMessage
  saveTestAgentConversationMessages: async (conversationId, clid, messages) => {
    try {
      const response = await api.post('/api/testing/agent/conversation-messages', {
        conversation_id: conversationId,
        clid,
        messages
      });
      return response.data;
    } catch (error) {
      console.error('Error saving test agent conversation messages:', error);
      return { success: false, error: error.response?.data?.detail || error.message };
    }
  },

  listTestPlans: async (clid) => {
    try {
      const response = await api.get('/api/testing/test-plans', { params: { clid } });
      return response.data;
    } catch (error) {
      console.error('Error listing test plans:', error);
      return { success: false, plans: [], error: error.response?.data?.detail || error.message };
    }
  },

  createTestPlan: async (payload) => {
    try {
      const response = await api.post('/api/testing/test-plans', payload);
      return response.data;
    } catch (error) {
      console.error('Error creating test plan:', error);
      return { success: false, error: error.response?.data?.detail || error.message };
    }
  },

  updateTestPlan: async (planId, payload) => {
    try {
      const response = await api.put(`/api/testing/test-plans/${planId}`, payload);
      return response.data;
    } catch (error) {
      console.error('Error updating test plan:', error);
      return { success: false, error: error.response?.data?.detail || error.message };
    }
  },

  deleteTestPlan: async (planId) => {
    try {
      const response = await api.delete(`/api/testing/test-plans/${planId}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting test plan:', error);
      return { success: false, error: error.response?.data?.detail || error.message };
    }
  },

  saveRunReport: async (payload) => {
    try {
      const response = await api.post('/api/testing/run-reports', payload);
      return response.data;
    } catch (error) {
      console.error('Error saving run report:', error);
      return { success: false, error: error.response?.data?.detail || error.message };
    }
  },

  listRunReports: async (clid, { planId, limit } = {}) => {
    try {
      const params = { clid };
      if (planId) params.plan_id = planId;
      if (limit) params.limit = limit;
      const response = await api.get('/api/testing/run-reports', { params });
      return response.data;
    } catch (error) {
      console.error('Error listing run reports:', error);
      return { success: false, reports: [], error: error.response?.data?.detail || error.message };
    }
  },

  getRunReport: async (reportId) => {
    try {
      const response = await api.get(`/api/testing/run-reports/${reportId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching run report:', error);
      return { success: false, error: error.response?.data?.detail || error.message };
    }
  },

  updateRunReportNotes: async (reportId, notes) => {
    try {
      const response = await api.patch(`/api/testing/run-reports/${reportId}`, { notes: notes ?? '' });
      return response.data;
    } catch (error) {
      console.error('Error updating run report notes:', error);
      return { success: false, error: error.response?.data?.detail || error.message };
    }
  },

  // Partial update of a saved run report (e.g. after re-running a single case).
  // Only the keys present in `payload` are applied server-side.
  updateRunReport: async (reportId, payload) => {
    try {
      const response = await api.patch(`/api/testing/run-reports/${reportId}`, payload || {});
      return response.data;
    } catch (error) {
      console.error('Error updating run report:', error);
      return { success: false, error: error.response?.data?.detail || error.message };
    }
  },

  deleteRunReport: async (reportId) => {
    try {
      const response = await api.delete(`/api/testing/run-reports/${reportId}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting run report:', error);
      return { success: false, error: error.response?.data?.detail || error.message };
    }
  },

  // AI-compare two saved run reports (A = before, B = after). Returns a
  // structured per-case verdict plus an overall summary.
  compareRunReports: async (reportIdA, reportIdB) => {
    try {
      const response = await api.post('/api/testing/run-reports/compare', {
        report_id_a: reportIdA,
        report_id_b: reportIdB,
      });
      return response.data;
    } catch (error) {
      console.error('Error comparing run reports:', error);
      return { success: false, error: error.response?.data?.detail || error.message };
    }
  },
};

// API functions for analytics
export const analyticsService = {
  // Get analytics events summary
  getEventsSummary: async (days = 7, clinicId = null) => {
    try {
      const params = { days };
      if (clinicId) {
        params.reference_id = clinicId;
      }

      const response = await api.get('/api/analytics/events/summary', {
        params
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching analytics summary:', error);
      throw error;
    }
  },

  // Get events by type
  getEventsByType: async (eventType, clinicId = null, limit = 100) => {
    try {
      const params = { limit };
      if (clinicId) {
        params.reference_id = clinicId;
      }

      const response = await api.get(`/api/analytics/events/by-type/${eventType}`, {
        params
      });
      return response.data;
    } catch (error) {
      console.error(`Error fetching events of type ${eventType}:`, error);
      throw error;
    }
  },

  // Export clinic analytics report as HTML
  exportClinicReportHtml: async (clinicId, days = 30, locale = 'zh-TW') => {
    try {
      const response = await api.get(`/api/analytics/clinic/${clinicId}/report/html`, {
        params: { days, locale },
        responseType: 'text'  // Get raw HTML content
      });
      return response.data;
    } catch (error) {
      console.error(`Error exporting clinic analytics report for ${clinicId}:`, error);
      throw error;
    }
  },

  // Get LLM token usage summary for a clinic (totals + by_model + by_day + cost USD)
  getLlmTokenUsageSummary: async (clinicId, days = 7) => {
    try {
      const response = await api.get('/api/analytics/llm-token-usage/summary', {
        params: { clinic_id: clinicId, days }
      });
      return response.data;
    } catch (error) {
      console.error(`Error fetching LLM token usage for ${clinicId}:`, error);
      throw error;
    }
  }
};

// API functions for Meta WhatsApp Phone Number
export const metaWhatsappPhoneNumberService = {
  // Get all phone numbers
  getAllPhoneNumbers: async () => {
    try {
      const response = await api.get('/api/settings/meta-whatsapp-phone-numbers');
      return response.data;
    } catch (error) {
      console.error('Error fetching Meta WhatsApp phone numbers:', error);
      throw error;
    }
  },

  // Get phone number by ID
  getPhoneNumberById: async (id) => {
    try {
      const response = await api.get(`/api/settings/meta-whatsapp-phone-numbers/${id}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching Meta WhatsApp phone number ${id}:`, error);
      throw error;
    }
  },

  // Create new phone number
  createPhoneNumber: async (phoneNumberData) => {
    try {
      const response = await api.post('/api/settings/meta-whatsapp-phone-numbers', phoneNumberData);
      return response.data;
    } catch (error) {
      console.error('Error creating Meta WhatsApp phone number:', error);
      throw error;
    }
  },

  // Update phone number
  updatePhoneNumber: async (id, phoneNumberData) => {
    try {
      const response = await api.put(`/api/settings/meta-whatsapp-phone-numbers/${id}`, phoneNumberData);
      return response.data;
    } catch (error) {
      console.error(`Error updating Meta WhatsApp phone number ${id}:`, error);
      throw error;
    }
  },

  // Delete phone number
  deletePhoneNumber: async (id) => {
    try {
      const response = await api.delete(`/api/settings/meta-whatsapp-phone-numbers/${id}`);
      return response.data;
    } catch (error) {
      console.error(`Error deleting Meta WhatsApp phone number ${id}:`, error);
      throw error;
    }
  }
};

// API functions for Twilio Phone Number
export const twilioPhoneNumberService = {
  // Get all phone numbers
  getAllPhoneNumbers: async () => {
    try {
      const response = await api.get('/api/settings/twilio-phone-numbers');
      return response.data;
    } catch (error) {
      console.error('Error fetching Twilio phone numbers:', error);
      throw error;
    }
  },

  // Get phone number by phone number
  getPhoneNumberByNumber: async (phoneNumber) => {
    try {
      const response = await api.get(`/api/settings/twilio-phone-numbers/${encodeURIComponent(phoneNumber)}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching Twilio phone number ${phoneNumber}:`, error);
      throw error;
    }
  },

  getUnusedPhoneNumbers: async () => {
    try {
      const response = await api.get('/api/settings/twilio-unused-phone-numbers');
      return response.data;
    } catch (error) {
      console.error('Error fetching unused Twilio phone numbers:', error);
      throw error;
    }
  },
  // Create new phone number
  createPhoneNumber: async (phoneNumberData) => {
    try {
      const response = await api.post('/api/settings/twilio-phone-numbers', phoneNumberData);
      return response.data;
    } catch (error) {
      console.error('Error creating Twilio phone number:', error);
      throw error;
    }
  },

  // Update phone number
  updatePhoneNumber: async (phoneNumber, phoneNumberData) => {
    try {
      const response = await api.put(`/api/settings/twilio-phone-numbers/${encodeURIComponent(phoneNumber)}`, phoneNumberData);
      return response.data;
    } catch (error) {
      console.error(`Error updating Twilio phone number ${phoneNumber}:`, error);
      throw error;
    }
  },

  // Delete phone number
  deletePhoneNumber: async (phoneNumber) => {
    try {
      const response = await api.delete(`/api/settings/twilio-phone-numbers/${encodeURIComponent(phoneNumber)}`);
      return response.data;
    } catch (error) {
      console.error(`Error deleting Twilio phone number ${phoneNumber}:`, error);
      throw error;
    }
  },

  // Refresh phone numbers from Twilio API
  refreshPhoneNumbers: async () => {
    try {
      const response = await api.get('/api/settings/twilio-available-phone-numbers');
      return response.data;
    } catch (error) {
      console.error('Error refreshing Twilio phone numbers:', error);
      throw error;
    }
  }
};

// API functions for WhatsApp Template
export const whatsappTemplateService = {
  // Create new WhatsApp template
  createTemplate: async (templateData) => {
    try {
      const response = await api.post('/api/settings/whatsapp-templates', templateData);
      return response.data;
    } catch (error) {
      console.error('Error creating WhatsApp template:', error);
      throw error;
    }
  },

  // Get template categories
  getCategories: async () => {
    try {
      const response = await api.get('/api/settings/whatsapp-templates/categories');
      return response.data;
    } catch (error) {
      console.error('Error fetching template categories:', error);
      throw error;
    }
  },

  // Get template languages
  getLanguages: async () => {
    try {
      const response = await api.get('/api/settings/whatsapp-templates/languages');
      return response.data;
    } catch (error) {
      console.error('Error fetching template languages:', error);
      throw error;
    }
  },

  // List templates for a specific WABA ID
  listTemplates: async (wabaId, fields = "id,name,category,language,status,components", limit = 100) => {
    try {
      const response = await api.get(`/api/settings/whatsapp-templates/${wabaId}/list`, {
        params: { fields, limit }
      });
      return response.data;
    } catch (error) {
      console.error('Error listing templates:', error);
      throw error;
    }
  },

  // Apply all Nexi templates to a specific WABA ID
  applyAllNexiTemplates: async (wabaId) => {
    try {
      const response = await api.post(`/api/settings/whatsapp-templates/${wabaId}/apply-all-nexi`);
      return response.data;
    } catch (error) {
      console.error('Error applying Nexi templates:', error);
      throw error;
    }
  }
};

// API functions for Nexi Global Template
export const nexiGlobalTemplateService = {
  // Get all templates with optional type filter
  getAllTemplates: async (type = null) => {
    try {
      const response = await api.get('/api/settings/nexi-global-templates', {
        params: type ? { template_type: type } : {}
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching Nexi global templates:', error);
      throw error;
    }
  },

  // Get template by ID
  getTemplateById: async (id) => {
    try {
      const response = await api.get(`/api/settings/nexi-global-templates/${id}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching Nexi global template ${id}:`, error);
      throw error;
    }
  },

  // Create new template
  createTemplate: async (templateData) => {
    try {
      const response = await api.post('/api/settings/nexi-global-templates', templateData);
      return response.data;
    } catch (error) {
      console.error('Error creating Nexi global template:', error);
      throw error;
    }
  },

  // Update template
  updateTemplate: async (id, templateData) => {
    try {
      const response = await api.put(`/api/settings/nexi-global-templates/${id}`, templateData);
      return response.data;
    } catch (error) {
      console.error(`Error updating Nexi global template ${id}:`, error);
      throw error;
    }
  },

  // Delete template
  deleteTemplate: async (id) => {
    try {
      const response = await api.delete(`/api/settings/nexi-global-templates/${id}`);
      return response.data;
    } catch (error) {
      console.error(`Error deleting Nexi global template ${id}:`, error);
      throw error;
    }
  },

  // Render template
  renderTemplate: async (id, languageCode, variables = null) => {
    try {
      const response = await api.get(`/api/settings/nexi-global-templates/${id}/render`, {
        params: {
          language_code: languageCode,
          ...(variables && { variables })
        }
      });
      return response.data;
    } catch (error) {
      console.error(`Error rendering Nexi global template ${id}:`, error);
      throw error;
    }
  },

  // Render template with content + metadata for preview (quick_reply_options, cta_url_options, list_options, type)
  renderTemplatePreview: async (templateId, languageCode, variables = null, clid = null) => {
    try {
      const params = {
        language_code: languageCode,
        ...(clid && { clid })
      };
      if (variables && typeof variables === 'object') {
        params.variables_json = JSON.stringify(variables);
      }
      const response = await api.get(`/api/settings/nexi-global-templates/${templateId}/render-preview`, { params });
      return response.data;
    } catch (error) {
      console.error(`Error rendering template preview ${templateId}:`, error);
      throw error;
    }
  }
};

// API functions for Action Items
export const actionItemsService = {
  // Get all action items
  getAllActionItems: async () => {
    try {
      const response = await api.get('/api/action-items/list');
      return response.data;
    } catch (error) {
      console.error('Error fetching action items:', error);
      throw error;
    }
  },

  // Get action items by type
  getActionItemsByType: async (type) => {
    try {
      const response = await api.get('/api/action-items/list', {
        params: { type }
      });
      return response.data;
    } catch (error) {
      console.error(`Error fetching action items of type ${type}:`, error);
      throw error;
    }
  },

  // Get agent deprecation action items
  getAgentDeprecationItems: async () => {
    try {
      const response = await api.get('/api/action-items/list', {
        params: { type: 'agent-deprecation' }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching agent deprecation action items:', error);
      throw error;
    }
  }
};

// API functions for Clinic Integration Events
export const clinicIntegrationEventsService = {
  listEvents: async (skip = 0, limit = 100, eventType = null, source = null, objectId = null, search = null, createdBy = null) => {
    try {
      const params = { skip, limit };
      if (eventType) params.event_type = eventType;
      if (source) params.source = source;
      if (objectId) params.object_id = objectId;
      if (search) params.search = search;
      if (createdBy) params.created_by = createdBy;

      const response = await api.get('/api/clinic-integration-events', { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching clinic integration events:', error);
      throw error;
    }
  },
  getApiKeyById: async (apiKeyId) => {
    try {
      const response = await api.get(`/api/clinic-integration-events/api-key/${apiKeyId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching API key:', error);
      throw error;
    }
  }
};

// API functions for Meta WhatsApp Events
export const metaWhatsappEventsService = {
  listEvents: async (skip = 0, limit = 100, eventType = null, search = null) => {
    try {
      const params = { skip, limit };
      if (eventType) params.event_type = eventType;
      if (search) params.search = search;

      const response = await api.get('/api/meta-whatsapp-events', { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching meta whatsapp events:', error);
      throw error;
    }
  }
};

// API functions for Onboarding
export const onboardingService = {
  // Get phone numbers by WABA ID
  getPhoneNumbersByWabaId: async (wabaId) => {
    try {
      console.log('Calling API to get phone numbers for WABA ID:', wabaId);
      const response = await api.post('/api/onboarding/phone-numbers', { waba_id: wabaId });
      console.log('API response:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error fetching phone numbers by WABA ID:', error);
      throw error;
    }
  },

  // Complete onboarding process
  completeOnboarding: async (wabaId, phoneNumberId, phoneNumberName, phoneNumber, clinicId) => {
    try {
      console.log('Calling API to complete onboarding:', { wabaId, phoneNumberId, phoneNumberName, phoneNumber, clinicId });
      const response = await api.post('/api/onboarding/complete', {
        clid: clinicId,
        waba_id: wabaId,
        phone_number_id: phoneNumberId,
        phone_number_name: phoneNumberName,
        phone_number: phoneNumber
      });
      console.log('API response:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error completing onboarding:', error);
      throw error;
    }
  },

  // Create tokenized onboarding link for clinic onboarding flow
  createOnboardingToken: async ({
    clid,
    name,
    locale = 'en_US',
    type = 'clinic',
    integration = null,
    referrer = null,
  }) => {
    try {
      const response = await api.post('/api/onboarding/create-token', {
        clid,
        name,
        locale,
        type,
        integration,
        referrer,
      });
      return response.data;
    } catch (error) {
      console.error('Error creating onboarding token:', error);
      throw error;
    }
  },

  // List onboarding tokens and saved answers
  listOnboardingTokens: async ({ clid = null, limit = 100 } = {}) => {
    try {
      const response = await api.get('/api/onboarding/tokens', {
        params: {
          clid: clid || undefined,
          limit
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error listing onboarding tokens:', error);
      throw error;
    }
  },

  // Create organization with clinic and onboarding token in one flow
  createOrganizationWithClinic: async ({
    organizationName,
    clinicName,
    clid,
    organizationId,
    region = 'HK',
    contactNumber = '',
    locale = 'en_US',
    integration = null,
    referrer = null,
    createQuotaWallet = true
  }) => {
    try {
      const response = await api.post('/api/onboarding/create-organization-with-clinic', {
        organization_name: organizationName,
        clinic_name: clinicName,
        clid: clid || undefined,
        organization_id: organizationId || undefined,
        region,
        contact_number: contactNumber,
        locale,
        integration: integration || undefined,
        referrer: referrer || undefined,
        create_quota_wallet: createQuotaWallet
      });
      return response.data;
    } catch (error) {
      console.error('Error creating organization with clinic:', error);
      throw error;
    }
  },

  // Start the WhatsApp onboarding conversation by registering the
  // SME owner phone <-> clid mapping and sending the
  // `nexi_onboarding_greeting` template from the central onboarding number.
  startOnboardingConversation: async ({
    token,
    ownerPhone,
    locale = null,
    clinicNameOverride = null,
  }) => {
    try {
      const response = await api.post('/api/onboarding/start-conversation', {
        token,
        owner_phone: ownerPhone,
        locale: locale || undefined,
        clinic_name_override: clinicNameOverride || undefined,
      });
      return response.data;
    } catch (error) {
      console.error('Error starting onboarding conversation:', error);
      throw error;
    }
  }
};

// API functions for Organization settings
export const organizationService = {
  getOrganization: async (organizationId) => {
    const response = await api.get(`/api/organizations/${organizationId}`);
    return response.data;
  },
  patchOrganization: async (organizationId, settingsPatch) => {
    const response = await api.patch(`/api/organizations/${organizationId}`, settingsPatch);
    return response.data;
  },
};

export default api;
